import type {
  PlantaMeta,
  AjustePlantaDTO,
  MovelMeta,
  MovelListItem,
  PecaAmbiguousResponse,
} from '@visualizador/shared';

/**
 * Cliente HTTP da API. Em dev o Vite faz proxy de /api -> :3000;
 * em producao a API serve o frontend no mesmo host. Por isso a base e relativa.
 */
const BASE = import.meta.env.VITE_API_BASE ?? '';

async function getJson<T>(url: string): Promise<{ status: number; data: T }> {
  const res = await fetch(`${BASE}${url}`);
  const data = (await res.json().catch(() => ({}))) as T;
  return { status: res.status, data };
}

function arquivoQS(arquivo?: string): string {
  return arquivo ? `?arquivo=${encodeURIComponent(arquivo)}` : '';
}

export const api = {
  /** Metadados do movel (arquivo desambigua quando o mesmo id existe em varios). `null` se 404. */
  async getMovel(movelId: string, arquivo?: string): Promise<MovelMeta | null> {
    const { status, data } = await getJson<{ ok: boolean; movel: MovelMeta }>(
      `/api/movel/${encodeURIComponent(movelId)}${arquivoQS(arquivo)}`,
    );
    return status === 200 && data.ok ? data.movel : null;
  },

  /**
   * URL do .glb para o GLTFLoader. `versao` (ex.: atualizado_em) entra como `?v=`
   * para "quebrar" o cache do navegador quando o modelo muda — como o binario e
   * servido com cache imutavel, mudar a versao gera uma URL nova e baixa o novo.
   */
  glbUrl(movelId: string, arquivo?: string, versao?: string, parte?: 'ferragens'): string {
    const qs = new URLSearchParams();
    if (arquivo) qs.set('arquivo', arquivo);
    if (versao) qs.set('v', versao);
    // "ferragens": o .glb separado das ferragens, baixado so quando mostrar.
    if (parte) qs.set('parte', parte);
    const s = qs.toString();
    return `${BASE}/api/movel/${encodeURIComponent(movelId)}/glb${s ? `?${s}` : ''}`;
  },

  /**
   * Resolve uma peca -> movel. `arquivo` (do QR) e `projeto` desambiguam quando a
   * mesma peca existe em mais de um movel.
   * Retorna { movelId, arquivo } quando unico, { ambiguous } quando ha varios, ou null se 404.
   *
   * `pieceIds` traz TODAS as ocorrencias da peca dentro do movel: o QR da
   * etiqueta carrega o id da entidade, e a mesma prateleira pode estar em 9
   * modulos iguais. A API so manda isso a partir do plugin 0.9.3 — quando nao
   * vier, a tela cai no id pedido (um destaque so), como sempre foi.
   */
  async resolvePeca(
    pieceId: string,
    opts: { arquivo?: string; projeto?: string } = {},
  ): Promise<
    | { kind: 'ok'; movelId: string; arquivo: string; pieceIds: string[] }
    | { kind: 'ambiguous'; options: PecaAmbiguousResponse['options'] }
    | { kind: 'notfound' }
  > {
    const qs = new URLSearchParams();
    if (opts.arquivo) qs.set('arquivo', opts.arquivo);
    if (opts.projeto) qs.set('projeto', opts.projeto);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const { status, data } = await getJson<
      { ok: boolean; movel_id?: string; arquivo?: string; piece_ids?: string[] } & Partial<PecaAmbiguousResponse>
    >(`/api/peca/${encodeURIComponent(pieceId)}${suffix}`);

    if (status === 200 && data.movel_id) {
      return {
        kind: 'ok',
        movelId: data.movel_id,
        arquivo: data.arquivo ?? '',
        pieceIds: Array.isArray(data.piece_ids) ? data.piece_ids : [],
      };
    }
    if (status === 300 && data.ambiguous) return { kind: 'ambiguous', options: data.options ?? [] };
    return { kind: 'notfound' };
  },

  /**
   * Exclui um movel (e o .glb). Devolve o STATUS: 401/403 significam que falta a
   * senha de administrador — a tela pede e tenta de novo.
   */
  async deleteMovel(movelId: string, arquivo?: string): Promise<number> {
    const res = await fetch(`${BASE}/api/movel/${encodeURIComponent(movelId)}${arquivoQS(arquivo)}`, {
      method: 'DELETE',
    });
    return res.status;
  },

  /** Lista paginada de moveis. */
  async listMoveis(
    busca: string,
    page: number,
    limit = 20,
  ): Promise<{ total: number; page: number; limit: number; items: MovelListItem[] }> {
    const qs = new URLSearchParams({ busca, page: String(page), limit: String(limit) });
    const { data } = await getJson<{
      ok: boolean;
      total: number;
      page: number;
      limit: number;
      items: MovelListItem[];
    }>(`/api/moveis?${qs.toString()}`);
    return { total: data.total ?? 0, page: data.page ?? 1, limit: data.limit ?? limit, items: data.items ?? [] };
  },

  // ---------- Planta baixa (piso da tela de projeto) ----------

  /**
   * Metadados da planta do cliente. A API responde 200 com `planta: null`
   * quando ainda nao existe planta (nao e erro), entao isto nao gera 404.
   */
  async getPlanta(arquivo: string): Promise<PlantaMeta | null> {
    const { status, data } = await getJson<{ ok: boolean; planta: PlantaMeta | null }>(
      `/api/planta/${encodeURIComponent(arquivo)}`,
    );
    return status === 200 && data.ok ? (data.planta ?? null) : null;
  },

  /** URL da imagem da planta. `versao` (atualizado_em) quebra o cache imutavel. */
  plantaImagemUrl(arquivo: string, versao?: string): string {
    const v = versao ? `?v=${encodeURIComponent(versao)}` : '';
    return `${BASE}/api/planta/${encodeURIComponent(arquivo)}/imagem${v}`;
  },

  /** Ajuste manual (posicao/escala/opacidade). Vence o que veio do SketchUp. */
  async ajustarPlanta(arquivo: string, dto: AjustePlantaDTO): Promise<{ status: number; planta: PlantaMeta | null }> {
    const res = await fetch(`${BASE}/api/planta/${encodeURIComponent(arquivo)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!res.ok) return { status: res.status, planta: null };
    const data = (await res.json()) as { ok: boolean; planta: PlantaMeta };
    return { status: res.status, planta: data.ok ? data.planta : null };
  },

  /** Apaga a planta do cliente (registro + binario). */
  async apagarPlanta(arquivo: string): Promise<number> {
    const res = await fetch(`${BASE}/api/planta/${encodeURIComponent(arquivo)}`, { method: 'DELETE' });
    return res.status;
  },
};
